import { gql } from "@apollo/client";
import { SCORECARD_FIELDS } from "./fragments";

// ─── Scorecards ─────────────────────────────────────────────────────────

export const GET_INTERVIEW_SCORECARDS = gql`
  query GetInterviewScorecards($interviewId: ID!) {
    interviewScorecards(interviewId: $interviewId) {
      id
      interviewId
      user {
        id
        firstName
        lastName
      }
      guide {
        id
      }
      guideSnapshot {
        guideId
        title
        division
        jobTitle
        status
        questionCount
      }
      status
      recommendation
      guideScore
      candidateQuestionScore
      overallScore
      createdAt
    }
  }
`;

export const GET_MY_SCORECARD = gql`
  ${SCORECARD_FIELDS}
  query GetMyScorecard($interviewId: ID!) {
    myScorecard(interviewId: $interviewId) {
      ...ScorecardFields
    }
  }
`;

export const ASSIGN_GUIDE = gql`
  ${SCORECARD_FIELDS}
  mutation AssignGuide($interviewId: ID!, $guideId: ID!) {
    interviewGuideAssign(interviewId: $interviewId, guideId: $guideId) {
      scorecard {
        ...ScorecardFields
      }
      errors {
        message
        code
      }
    }
  }
`;

// Self-provision the caller's own scorecard for the interview's already-assigned
// guide. Used when myScorecard is empty (e.g. a Reviewer added to the panel after
// the guide was assigned) — the backend derives the guide from the snapshot and
// only provisions a panel member, so no manager-only assign is needed.
export const ENSURE_SCORECARD = gql`
  ${SCORECARD_FIELDS}
  mutation EnsureScorecard($interviewId: ID!) {
    scorecardEnsure(interviewId: $interviewId) {
      scorecard {
        ...ScorecardFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const REMOVE_GUIDE = gql`
  mutation RemoveGuide($interviewId: ID!) {
    interviewGuideRemove(interviewId: $interviewId) {
      scorecard {
        id
      }
      errors {
        message
        code
      }
    }
  }
`;

export const SAVE_SCORECARD = gql`
  ${SCORECARD_FIELDS}
  mutation SaveScorecard($interviewId: ID!, $input: ScorecardSaveInput!) {
    scorecardSave(interviewId: $interviewId, input: $input) {
      scorecard {
        ...ScorecardFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const SUBMIT_SCORECARD = gql`
  ${SCORECARD_FIELDS}
  mutation SubmitScorecard($interviewId: ID!, $input: ScorecardSubmitInput!) {
    scorecardSubmit(interviewId: $interviewId, input: $input) {
      scorecard {
        ...ScorecardFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const ADD_SCORECARD_QUESTION = gql`
  ${SCORECARD_FIELDS}
  mutation AddScorecardQuestion(
    $interviewId: ID!
    $input: ScorecardQuestionAddInput!
  ) {
    scorecardQuestionAdd(interviewId: $interviewId, input: $input) {
      scorecard {
        ...ScorecardFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const REMOVE_SCORECARD_QUESTION = gql`
  ${SCORECARD_FIELDS}
  mutation RemoveScorecardQuestion($interviewId: ID!, $id: ID!) {
    scorecardQuestionRemove(interviewId: $interviewId, id: $id) {
      scorecard {
        ...ScorecardFields
      }
      errors {
        message
        code
      }
    }
  }
`;

// ─── Transcripts ────────────────────────────────────────────────────────

export const DELETE_TRANSCRIPT = gql`
  mutation DeleteTranscript($interviewId: ID!) {
    transcriptDelete(interviewId: $interviewId) {
      transcript {
        id
      }
      errors {
        message
        code
      }
    }
  }
`;

export const UPLOAD_TRANSCRIPT = gql`
  mutation UploadTranscript($interviewId: ID!, $input: TranscriptUploadInput!) {
    transcriptUpload(interviewId: $interviewId, input: $input) {
      transcript {
        id
        interviewId
        text
        textHtml
        fileName
        createdAt
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GET_TRANSCRIPT_FILE_URL = gql`
  query GetTranscriptFileUrl($interviewId: ID!) {
    interviewTranscript(interviewId: $interviewId) {
      id
      fileDownloadUrl
    }
  }
`;

export const EXTRACT_TRANSCRIPT_TEXT = gql`
  mutation ExtractTranscriptText($interviewId: ID!) {
    transcriptExtractText(interviewId: $interviewId) {
      transcript {
        id
        text
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GENERATE_SUMMARY = gql`
  mutation GenerateSummary($interviewId: ID!) {
    transcriptGenerateSummary(interviewId: $interviewId) {
      transcript {
        id
        summaryStatus
        summary {
          summary
          topicSummaries {
            topic
            detail
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GENERATE_AI_SCORE = gql`
  mutation GenerateAIScore($interviewId: ID!) {
    transcriptGenerateAIScore(interviewId: $interviewId) {
      transcript {
        id
        aiAnalysisStatus
        aiAnalysis {
          summary
          keyStrengths
          areasOfConcern
          questionScores {
            questionId
            score
            reasoning
            questionText
            notAsked
          }
          recommendation
          overallComment
          weightedScore
        }
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GET_INTERVIEW_TRANSCRIPT = gql`
  query GetInterviewTranscript($interviewId: ID!) {
    interviewTranscript(interviewId: $interviewId) {
      id
      interviewId
      aiAnalysisStatus
      summaryStatus
      uploadedBy {
        id
        firstName
        lastName
      }
      text
      textHtml
      fileName
      summary {
        summary
        topicSummaries {
          topic
          detail
        }
      }
      aiAnalysis {
        summary
        keyStrengths
        areasOfConcern
        questionScores {
          questionId
          score
          reasoning
          questionText
          notAsked
        }
        recommendation
        overallComment
        weightedScore
      }
      createdAt
    }
  }
`;
